import React, { useEffect, useState, useImperativeHandle, useMemo, useRef } from "react";
import AddIcon from "@mui/icons-material/Add";
import PetsIcon from "@mui/icons-material/Pets";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  InputAdornment,
  Drawer,
  Chip,
  Autocomplete,
} from "@mui/material";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { FiCalendar, FiClock, FiArrowLeft, FiEye, FiSend } from "react-icons/fi";
import { MdCurrencyRupee, MdPets } from "react-icons/md";
import { parse, isBefore } from "date-fns";
import PetCard from "./PetCard";
import { useToken } from "../../hooks/token/useToken";
import AddNewPet from "./AddNewPet";
import { useNavigate } from "react-router-dom";

import {useProfile} from "../../context/ProfileContext";
import { useAllowPastBooking } from "../../hooks/useAllowPastBooking";

// Helper functions
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [time, modifier] = timeStr.trim().split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (modifier === 'PM' && hours !== 12) hours += 12;
  if (modifier === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const parseDurationToMinutes = (durationStr) => {
  if (!durationStr || typeof durationStr !== 'string') return 0;
  const lower = durationStr.toLowerCase();
  let total = 0;
  const hourMatch = lower.match(/(\d+)\s*hour/);
  const minuteMatch = lower.match(/(\d+)\s*minute/);
  if (hourMatch) total += parseInt(hourMatch[1]) * 60;
  if (minuteMatch) total += parseInt(minuteMatch[1]);
  return total;
};

const formatTimeSlot = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return new Date(0, 0, 0, hours, minutes).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getNextTimeSlot = () => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const start = 8 * 60;
  const end = 20 * 60;
  if (currentMinutes < start) return formatTimeSlot(start);
  if (currentMinutes >= end) return null;
  const nextSlotMinutes = Math.ceil(currentMinutes / 15) * 15;
  if (nextSlotMinutes >= end) return null;
  return formatTimeSlot(nextSlotMinutes);
};

// ---------- Forms Helpers ----------
const normalizeConsentName = (value = "") =>
  String(value).trim().toLowerCase().replace(/\s+/g, " ");

const getFormRequirement = (form = {}) => {
  const req = form?.requirement;
  if (req === "mandatory") return "mandatory";
  if (typeof form.requirement === "boolean") {
    return form.requirement ? "mandatory" : "optional";
  }
  return "optional";
};

/**
 * Maps a human-readable service_type_name to the API's service category key.
 * The API's `services` field uses identifiers like "grooming", "pet_vet", etc.
 */
const getServiceCategoryKey = (serviceTypeName = "") => {
  const normalized = String(serviceTypeName).trim().toLowerCase();
  if (normalized.includes("grooming")) return "grooming";
  if (normalized.includes("vet") || normalized.includes("pet_vet")) return "pet_vet";
  if (normalized.includes("boarding") || normalized.includes("resort")) return "boarding";
  if (normalized.includes("day care") || normalized.includes("daycare")) return "day_care";
  if (normalized.includes("training")) return "training";
  if (normalized.includes("shop")) return "pet_shop";
  return normalized.replace(/\s+/g, "_");
};

// ---------- Main Component ----------
const VetClinicDrawer = React.forwardRef(
  (
    {
      isOpen,
      onClose,
      onUpdate,
      clientId,
      itemData,
      orderId,
      fromCalendar = false,
      selectedSlotInfo,
      editMode = false,
      onRefreshData = null,
      appointmentStatusRef = null,
      serviceTypeId = null,
      onPetFormStateChange = null,
      autoOpenPetForm = false,
      renderAsContent = false,
      isPetDetails = false,
      existingAppointments = [],
      checkEmployeeDoubleBooking = null,
      setSaveEnquiryClicked = null,
    },
    ref,
  ) => {
    const { allowPastBooking, maxPastDays, minDate, minDateStr } = useAllowPastBooking();
    const isPastTimeSlot = (slotLabel, selectedDate) => {
      if (allowPastBooking) return false;
      if (!selectedDate) return false;
      try {
        const slotDate = parse(
          `${selectedDate} ${slotLabel}`,
          "yyyy-MM-dd h:mm a",
          new Date(),
        );
        return isBefore(slotDate, new Date());
      } catch {
        return false;
      }
    };

    // ─── BUSINESS HOURS HELPERS ──────────────────────────────────────────────
    const time12ToMinutes = (timeStr) => {
      const [time, modifier] = timeStr.trim().split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    // Helper to generate 15‑minute slots from an array of { start, end } intervals
    const generateSlotsFromIntervals = (intervals) => {
      const slots = [];
      intervals.forEach((interval) => {
        let current = time12ToMinutes(interval.start);
        const end = time12ToMinutes(interval.end);
        while (current < end) {
          const h = Math.floor(current / 60);
          const m = current % 60;
          const period = h >= 12 ? "PM" : "AM";
          const displayHour = h % 12 || 12;
          slots.push(`${displayHour}:${m.toString().padStart(2, "0")} ${period}`);
          current += 15;
        }
      });
      return slots;
    };

    const generateDynamicSlotsForDate = (dateStr) => {
      if (!businessHours || !dateStr) return [];

      // --- Check for a special day first (overrides weekly schedule) ---
      const specialDay = businessHours.specialDays?.find(
        (d) => new Date(d.date).toISOString().split("T")[0] === dateStr
      );

      if (specialDay) {
        // Case 1: Day is open
        if (specialDay.isOpen) {
          // No custom intervals → full day, use default hours
          if (!specialDay.slots || specialDay.slots.length === 0) {
            return generateSlotsFromIntervals(businessHours.timeSlots);
          }
          // Custom intervals provided → only those slots are available
          return generateSlotsFromIntervals(specialDay.slots);
        }

        // Case 2: Day is marked closed
        if (!specialDay.slots || specialDay.slots.length === 0) {
          // Fully closed – no slots
          return [];
        }

        // Slots exist even though isOpen is false → treat them as blocked periods
        // Generate default slots and remove any that fall inside a blocked range
        const defaultSlots = generateSlotsFromIntervals(businessHours.timeSlots);
        const blockedRanges = specialDay.slots.map((slot) => ({
          start: time12ToMinutes(slot.start),
          end: time12ToMinutes(slot.end),
        }));
        return defaultSlots.filter((slotLabel) => {
          const slotMinutes = time12ToMinutes(slotLabel);
          return !blockedRanges.some(
            (range) => slotMinutes >= range.start && slotMinutes < range.end
          );
        });
      }

      const dayName = new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "long",
      });
      const daySchedule = businessHours.weeklySchedule.find(
        (d) => d.day === dayName
      );
      if (!daySchedule || !daySchedule.isOpen) return [];
      return generateSlotsFromIntervals(businessHours.timeSlots);
    };

    const getNextTimeSlot = () => {
      if (!businessHours) return "";
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const dayName = now.toLocaleDateString("en-US", { weekday: "long" });
      const daySchedule = businessHours.weeklySchedule.find(
        (d) => d.day === dayName,
      );
      if (!daySchedule || !daySchedule.isOpen) return "";

      for (let interval of businessHours.timeSlots) {
        const start = time12ToMinutes(interval.start);
        const end = time12ToMinutes(interval.end);
        if (currentMinutes >= end) continue;
        const nextSlot = currentMinutes < start ? start : Math.ceil(currentMinutes / 15) * 15;
        if (nextSlot < end) {
          const h = Math.floor(nextSlot / 60);
          const m = nextSlot % 60;
          const period = h >= 12 ? "PM" : "AM";
          const displayHour = h % 12 || 12;
          return `${displayHour}:${m.toString().padStart(2, "0")} ${period}`;
        }
      }
      return "";
    };

    // ─── STATE DECLARATIONS ──────────────────────────────────────────────────
    const [staffs, setStaffs] = useState([]);
    const [pets, setPets] = useState([]);
    const [vendorVetClinicServices, setVendorVetClinicServices] = useState([]);
    const [businessHours, setBusinessHours] = useState(null);

    const activeVisitTypes = useMemo(
      () =>
        vendorVetClinicServices.filter(
          (s) =>
            s.status === "active" &&
            (s.is_configured === true || s.is_configured === undefined),
        ),
      [vendorVetClinicServices],
    );
    const [isPetDisabled, setIsPetDisabled] = useState(false);

    const [showServiceNotes, setShowServiceNotes] = useState(false);
    const [showServiceDetailsModal, setShowServiceDetailsModal] = useState(false);
    const [serviceDetailData, setServiceDetailData] = useState(null);

    const { isEmployee, decodedData } = useToken();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showNewPetForm, setShowNewPetForm] = useState(
      autoOpenPetForm || false,
    );
    const [isEditPet, setIsEditPet] = useState(false);
    const [newPet, setNewPet] = useState({
      type: "",
      name: "",
      gender: "",
      size: "",
      breed: "",
      age: "",
      birth_date: "",
      notes: "",
      tags: [],
      weight_kg: "",
      behavior_notes: "",
    });

    const [petService, setPetService] = useState({
      pet_id: "",
      pet_name: "",
      appointment_date: "",
      appointment_start_time: "",
      vet_service: "",
      service_id: "",
      duration: "",
      price: 0,
      discount: 0,
      gst: 0,
      gst_amount: 0,
      totalAmount: 0,
      symptoms: "",
      appointment_to: "",
      service_name: "",
      service_category_name: "",
      service_type_name: "",
      service_details: null,
    });
    
    const autoSelectedPetRef = useRef(false);
    const navigate = useNavigate();

    const [editDataFetched, setEditDataFetched] = useState(false);
    const [petDetails, setPetDetails] = useState("");
    const [staffAvailableMessage, setStaffAvailableMessage] = useState("");

    // ─── FORMS STATE (direct integration) ────────────────────────────────────
    const [availableForms, setAvailableForms] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    const [formInputValue, setFormInputValue] = useState("");
    const [loadingForms, setLoadingForms] = useState(false);
    const [sendingForms, setSendingForms] = useState(false);
    const [sentFormNames, setSentFormNames] = useState([]);
    const preselectedFormIdsRef = React.useRef(new Set());


    // ─── FORMS FUNCTIONS ─────────────────────────────────────────────────────
    const fetchForms = async () => {
      if (!clientId || !petService.pet_id) {
        setAvailableForms([]);
        setSelectedForms([]);
        return;
      }

      setLoadingForms(true);
      try {
        // ✅ PRIORITY 1: use the actual service category ID from the selected service
        const categoryId = serviceDetailData?.service_category?._id;
        // ✅ PRIORITY 2: use the serviceTypeId prop (if provided)
        // ✅ PRIORITY 3: fallback to derived key (e.g., "pet_vet")
        const serviceIdentifier = categoryId || serviceTypeId || getServiceCategoryKey(petService.service_type_name || "Vet Clinic");

        const params = new URLSearchParams({
          clientId,
          petId: petService.pet_id,
          serviceName: serviceIdentifier, // now sends ID (like grooming drawer)
        });

        const res = await httpService.get(
          `/consentForm/getavailableFormsForBooking?${params.toString()}`
        );

        const formsArray = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const normalizedForms = formsArray.map((f) => ({
          ...f,
          _id: f.form_id,
          form_name: f.form_name,
        }));

        // Optional client-side filter (same as grooming)
        const serviceCategoryKey = getServiceCategoryKey(petService.service_type_name || "Vet Clinic");
        const filteredForms = normalizedForms.filter((f) => {
          if (!Array.isArray(f.services) || f.services.length === 0) return true;
          return f.services.some((s) => {
            const str = String(s).trim();
            return (
              str === String(serviceIdentifier) ||
              str.toLowerCase() === serviceCategoryKey
            );
          });
        });

        setAvailableForms(filteredForms);

        // Mandatory forms
        const mandatoryForms = filteredForms.filter(
          (f) => f.requirement === "mandatory"
        );
        const mandatoryIds = new Set(mandatoryForms.map((f) => f._id));

        // Preselected from order (edit mode)
        const preselectedIds = new Set([
          ...(Array.isArray(itemData?.form_ids) ? itemData.form_ids : []),
          ...Array.from(preselectedFormIdsRef.current || new Set()),
        ]);

        const allSelectedIds = new Set([...mandatoryIds, ...preselectedIds]);
        const selected = filteredForms.filter((f) =>
          allSelectedIds.has(f._id)
        );

        setSelectedForms(selected);
      } catch (error) {
        console.error("❌ Error fetching booking forms:", error);
        setAvailableForms([]);
        setSelectedForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    useEffect(() => {
      if (isOpen && clientId && petService.pet_id && petService.service_type_name) {
        fetchForms();
      } else {
        setAvailableForms([]);
        setSelectedForms([]);
      }
    }, [
      isOpen,
      clientId,
      petService.pet_id,
      petService.service_type_name,
      serviceDetailData,   // ✅ re-fetch when service details load (so category ID is available)
      serviceTypeId,       // ✅ re-fetch if prop changes
    ]);

    useEffect(() => {
      if (isOpen) {
        fetchSentForms();
      }
    }, [isOpen]);

    const fetchSentForms = async () => {
      const resolvedClientId =
        clientId ||
        itemData?.customer_id ||
        itemData?.vendor_client_id ||
        itemData?.client_id;
        
      if (!resolvedClientId) return;

      try {
        // Add a timestamp cache-buster to the URL to prevent 304 Not Modified caching
        const timestamp = new Date().getTime();
        const res = await httpService.get(
          `/vendorclient/getformdetails/${resolvedClientId}?t=${timestamp}`
        );
        
        const forms = res.data?.data ?? res.data ?? [];
        const sentNames = forms
          .filter((form) => form.status?.toLowerCase() === "sent")
          .map((form) => form.form_name)
          .filter(Boolean);
          
        setSentFormNames(sentNames);
      } catch (error) {
        console.error("Error fetching client form details:", error);
      }
    };  

    // ─── EFFECTS FOR FORMS ──────────────────────────────────────────────────
    useEffect(() => {
      // Only fetch forms when the drawer is open, we have a client, and a pet is selected
      if (isOpen && clientId && petService.pet_id) {
        fetchForms();
      } else {
        // Clear forms if a pet is deselected
        setAvailableForms([]);
        setSelectedForms([]);
      }
    }, [
      isOpen, 
      clientId, 
      petService.pet_id, 
      petService.service_name, 
      petService.service_type_name
    ]);

    useEffect(() => {
      if (isOpen && clientId) {
        fetchSentForms();
      }
    }, [isOpen, clientId, itemData?._id]);

    useEffect(() => {
      if (itemData?.form_ids && Array.isArray(itemData.form_ids)) {
        preselectedFormIdsRef.current = new Set(itemData.form_ids);
      }
    }, [itemData?.form_ids]);

    // ─── OTHER EFFECTS ──────────────────────────────────────────────────────
    useEffect(() => {
      if (isPetDetails && (itemData?.pet_id || itemData?.vendor_pet_id)) {
        setIsPetDisabled(true);
        if (!petService.pet_id && (itemData.pet_id || itemData.vendor_pet_id)) {
          setPetService((prev) => ({
            ...prev,
            pet_id: itemData.pet_id || itemData.vendor_pet_id,
          }));
          fetchPetDetails(itemData.pet_id || itemData.vendor_pet_id);
        }
      }
    }, [isPetDetails, itemData, petService.pet_id]);

    useEffect(() => {
      if (
        isOpen &&
        !itemData.start_date &&
        !itemData.start_time &&
        !selectedSlotInfo?.date &&
        !selectedSlotInfo?.startTime
      ) {
        setPetService((prev) => ({
          ...prev,
          appointment_start_time: getNextTimeSlot(),
          appointment_date: new Date().toISOString().split("T")[0],
        }));
      }
    }, [isOpen, businessHours]);

    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          const petsRes = await httpService.get(
            `/vendorpet/getvendorclientpets/${clientId}`,
          );
          setPets(petsRes.data);
        } catch (error) {
          console.error("Error loading initial data", error);
        }
      };
      fetchInitialData();
    }, [clientId, serviceTypeId]);

    useEffect(() => {
      if (
        selectedSlotInfo &&
        selectedSlotInfo.date &&
        selectedSlotInfo.startTime
      ) {
        setPetService((prev) => ({
          ...prev,
          appointment_date: selectedSlotInfo.date,
          appointment_start_time: selectedSlotInfo.startTime.toUpperCase(),
        }));
      }
    }, [selectedSlotInfo]);

    useEffect(() => {
      const fetchServices = async () => {
        try {
          const petType = petDetails?.pet_type?.name;
          const appointmentDate = petService.appointment_date || null; // ✅ Get appointment date

          // ✅ Use POST instead of GET
          const response = await httpService.post(
            `/vendorvetclinicservice/getvendorvetclinicservices`,
            {
              status: "active",
              petType: petType || null,
              appointmentDate: appointmentDate, // ✅ Pass appointment date
            }
          );
          
          const services = response?.data || [];

          const filteredServices = services.filter((service) => {
            if (service.status !== "active") return false;
            if (service.is_configured === false) return false;

            const currentPetType = petType?.toLowerCase()?.trim();
            if (!currentPetType) return true;

            if (!service.pet_type || service.pet_type.length === 0) {
              return true;
            }

            let servicePetTypes = [];
            if (Array.isArray(service.pet_type)) {
              servicePetTypes = service.pet_type
                .map((pt) =>
                  typeof pt === "object"
                    ? pt?.name?.toLowerCase()?.trim()
                    : String(pt).toLowerCase().trim(),
                )
                .filter(Boolean);
            } else if (typeof service.pet_type === "string") {
              servicePetTypes = service.pet_type
                .toLowerCase()
                .split(",")
                .map((pt) => pt.trim());
            }

            if (servicePetTypes.length === 0) return true;
            return (
              servicePetTypes.includes("both") ||
              servicePetTypes.includes(currentPetType)
            );
          });
          
          setVendorVetClinicServices(filteredServices);
          
          // ✅ If a service was previously selected, re-fetch its details with new date
          if (petService.service_id && filteredServices.length > 0) {
            const stillExists = filteredServices.some(s => s._id === petService.service_id);
            if (stillExists) {
              await fetchVetClinicServiceDetails(petService.service_id);
            } else {
              setPetService(prev => ({ ...prev, service_id: "" }));
              setServiceDetailData(null);
            }
          }
          
          if (!filteredServices.length && petType) {
            toast.warn(`No vet clinic services found for pet type "${petType}".`);
          }
        } catch (error) {
          console.error("Error fetching vet clinic services:", error);
        }
      };
      if (petDetails?._id) fetchServices();
    }, [petDetails?._id, serviceTypeId, petService.appointment_date]);

    useEffect(() => {
      if (isOpen) {
        const fetchBusinessHours = async () => {
          try {
            const res = await httpService.get("/vethours/getvetbusinesshours");
            const data = res.data?.data || res.data || null;
            setBusinessHours(data);
          } catch (err) {
            console.error("Failed to fetch vet business hours:", err);
            setBusinessHours(null);
          }
        };
        autoSelectedPetRef.current = false;
        fetchBusinessHours();
      }
    }, [isOpen]);

    useEffect(() => {
      // Only run when the drawer is open
      if (!isOpen) return;
      
      // Only auto-select once per session
      if (autoSelectedPetRef.current) return;
      
      // Don't override an already selected pet
      if (petService.pet_id) return;
      
      // Wait until the pets list is available
      if (!pets || pets.length === 0) return;

      // Consider only active pets (matching the dropdown filter)
      const activePets = pets.filter(pet => pet.status?.toLowerCase() !== 'inactive');
      
      if (activePets.length === 1) {
        const singlePet = activePets[0];
        setPetService(prev => ({ ...prev, pet_id: singlePet._id }));
        fetchPetDetails(singlePet._id);
        autoSelectedPetRef.current = true; // prevent future auto‑selection
      }
    }, [isOpen, pets, petService.pet_id]);

    function getMinutes(durationStr) {
      if (!durationStr) return 0;
      const str = durationStr.toLowerCase();
      let total = 0;
      const hourMatch = str.match(/(\d+)\s*h/);
      const minMatch = str.match(/(\d+)\s*m(?!in)/);
      const minuteMatch = str.match(/(\d+)\s*min/);
      if (hourMatch) total += parseInt(hourMatch[1]) * 60;
      if (minMatch) total += parseInt(minMatch[1]);
      if (minuteMatch) total += parseInt(minuteMatch[1]);
      if (total === 0) {
        const raw = str.match(/(\d+)/);
        if (raw) total = parseInt(raw[1]);
      }
      return total;
    }

    function getTimeRange(startTime12h, durationMinutes) {
      const match = startTime12h.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
      if (!match) throw new Error("Invalid time format.");
      let [_, hour, minute, period] = match;
      hour = parseInt(hour, 10);
      minute = parseInt(minute, 10);
      period = period.toLowerCase();
      if (period === "pm" && hour !== 12) hour += 12;
      if (period === "am" && hour === 12) hour = 0;
      const startDate = new Date();
      startDate.setHours(hour, minute, 0, 0);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      const formatTime = (date) => {
        const h = date.getHours().toString().padStart(2, "0");
        const m = date.getMinutes().toString().padStart(2, "0");
        return `${h}:${m}`;
      };
      return `${formatTime(startDate)}-${formatTime(endDate)}`;
    }

    useEffect(() => {
      const getAvailableVendorStaffs = async () => {
        const timeSlots =
          petService.appointment_start_time && petService.duration
            ? getTimeRange(
                petService.appointment_start_time,
                getMinutes(petService.duration),
              )
            : null;
        if (timeSlots === null) return;
        const payload = {
          date: petService.appointment_date || null,
          timeSlots,
          serviceCategoryTypeId: serviceTypeId,
          appointmentId: itemData.ref_id || null,
        };
        try {
          const response = await httpService.post(
            "/employees/getAvailableVendorEmployees",
            payload,
          );
          const filteredStaffs = response.data.filter(
            (item) => item.is_available === true || item.is_available == null,
          );
          setStaffs(filteredStaffs);
          if (isEmployee) {
            const loggedInEmployeeId = decodedData?.data?.user?.employee_id;
            const isStaffAvailable = filteredStaffs.some(
              (item) => item._id == loggedInEmployeeId,
            );
            if (!isStaffAvailable) {
              toast.warn(
                "You are not available at the selected time. Please choose a different time slot!!",
              );
              setPetService((prev) => ({ ...prev, appointment_to: "" }));
            } else {
              setPetService((prev) => ({
                ...prev,
                appointment_to: loggedInEmployeeId,
              }));
            }
          } else {
            if (filteredStaffs.length === 0 && timeSlots) {
              toast.warn(
                "No Veterinarian staff available for the selected time slot!!",
              );
              setStaffAvailableMessage(
                "No Veterinarian Staff available for the selected time slot!! Please select a different timeslot.",
              );
              setPetService((prev) => ({ ...prev, appointment_to: "" }));
            } else {
              if (petService.appointment_to != "") {
                const isSelectedStaffAvailable = filteredStaffs.some(
                  (item) => item._id == petService.appointment_to,
                );
                if (!isSelectedStaffAvailable) {
                  setPetService((prev) => ({ ...prev, appointment_to: "" }));
                }
              }
              setStaffAvailableMessage("");
            }
          }
        } catch (error) {
          console.error("Error checking staff occupancy:", error);
        }
      };
      getAvailableVendorStaffs();
    }, [
      petService.appointment_date,
      petService.appointment_start_time,
      petService.duration,
    ]);

    const handlePetServiceChange = (field, value) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (field === "appointment_date") {
        const checkinDate = new Date(value);
        checkinDate.setHours(0, 0, 0, 0);
        // if (checkinDate < today) {
        //   toast.error("Check-in date cannot be in the past.");
        //   return false;
        // }
        if (checkinDate < minDate) {
          toast.error(
            allowPastBooking
              ? `You can book only up to ${maxPastDays} day(s) in the past.`
              : "Appointment date cannot be in the past.",
          );
          return false;
        }

        if (businessHours) {
          const dayName = new Date(value).toLocaleDateString("en-US", {
            weekday: "long",
          });
          const daySchedule = businessHours.weeklySchedule.find(
            (d) => d.day === dayName,
          );
          if (daySchedule && !daySchedule.isOpen) {
            const specialDay = businessHours.specialDays?.find(
              (d) => new Date(d.date).toISOString().split("T")[0] === value,
            );
            if (specialDay && specialDay.isOpen && specialDay.slots.length > 0) {
              // return true;
            }
            else {
              toast.error(
                `We are closed on ${dayName}s. Please choose an active business day.`,
              );
              return false;
            }
          }
          const specialDay = businessHours.specialDays?.find(
            (d) => new Date(d.date).toISOString().split("T")[0] === value,
          );
          if (specialDay && !specialDay.isOpen && specialDay.slots.length === 0) {
            toast.error(
              `We are closed on ${value}${
                specialDay.label ? ` (${specialDay.label})` : ""
              }. Please choose another date.`,
            );
            return false;
          }
        }
      }
      setPetService((prev) => ({ ...prev, [field]: value }));
      return true;
    };

    const resetFormData = () => {
      setPetDetails("");
      setServiceDetailData(null);
      setPetService({
        pet_id: "",
        appointment_date: "",
        appointment_start_time: "",
        vet_service: "",
        service_id: "",
        duration: "",
        price: 0,
        discount: 0,
        gst: 0,
        gst_amount: 0,
        totalAmount: 0,
        symptoms: "",
        appointment_to: "",
        service_name: "",
        service_category_name: "",
        service_type_name: "",
        service_details: null,
      });
      setEditDataFetched(false);
      setShowServiceNotes(false);
      setShowServiceDetailsModal(false);
      // Reset forms
      setSelectedForms([]);
      setAvailableForms([]);
      setSentFormNames([]);
      preselectedFormIdsRef.current = new Set();
    };

    useImperativeHandle(ref, () => ({
      closePetForm: () => setShowNewPetForm(false),
      resetFormData: () => resetFormData(),
    }));

    useEffect(() => {
      if (onPetFormStateChange) onPetFormStateChange(showNewPetForm);
    }, [showNewPetForm, onPetFormStateChange]);

    useEffect(() => {
      if (
        editMode &&
        itemData &&
        Object.keys(itemData).length > 0 &&
        !editDataFetched
      ) {
        const petNameFromData = itemData.pet_name || "";
        const formatDateForInput = (dateString) => {
          if (!dateString) return "";
          try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
            return new Date(dateString).toISOString().split("T")[0];
          } catch {
            return "";
          }
        };
        const convertTimeTo12Hour = (time24Hour) => {
          if (!time24Hour) return "";
          try {
            if (/\d{1,2}:\d{2}\s*(am|pm)/i.test(time24Hour)) {
              return time24Hour
                .replace(/\s*am\s*$/i, " AM")
                .replace(/\s*pm\s*$/i, " PM");
            }
            const hour = parseInt(time24Hour);
            if (isNaN(hour) || hour < 0 || hour > 23) return time24Hour;
            const period = hour >= 12 ? "PM" : "AM";
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}:00 ${period}`;
          } catch {
            return time24Hour;
          }
        };
        const technicianId =
          itemData.technician_id ||
          itemData.appointment_to ||
          itemData.employee_id ||
          itemData._originalAppointment?.technician_id ||
          itemData._originalOrderItem?.technician_id;

        const resolvedTime =
          convertTimeTo12Hour(itemData.start_time) ||
          convertTimeTo12Hour(itemData.appointment_time) ||
          "";
        setPetService((prev) => ({
          ...prev,
          pet_id: itemData.vendor_pet_id || itemData.pet_id || "",
          pet_name: petNameFromData,
          appointment_date:
            formatDateForInput(itemData.start_date) ||
            formatDateForInput(itemData.appointment_date) ||
            "",
          appointment_start_time: resolvedTime,
          appointment_to: technicianId || "",
          symptoms:
            itemData.vet_details?.symptoms ||
            itemData.notes ||
            itemData.symptoms ||
            "",
        }));

        if (itemData.vendor_pet_id || itemData.pet_id) {
          fetchPetDetails(itemData.vendor_pet_id || itemData.pet_id);
        } else if (petNameFromData && pets.length > 0) {
          const foundPet = pets.find((pet) => pet.name === petNameFromData);
          if (foundPet) {
            setPetService((prev) => ({ ...prev, pet_id: foundPet._id }));
            fetchPetDetails(foundPet._id);
          }
        }
        setEditDataFetched(true);
      }
    }, [editMode, itemData, editDataFetched, pets]);

    useEffect(() => {
      if (pets.length > 0 && !petService.pet_id && petService.pet_name) {
        const foundPet = pets.find((pet) => pet.name === petService.pet_name);
        if (foundPet) {
          setPetService((prev) => ({ ...prev, pet_id: foundPet._id }));
          fetchPetDetails(foundPet._id);
        }
      }
    }, [pets, petService.pet_id, petService.pet_name]);

    useEffect(() => {
      if (
        editMode &&
        itemData?.service_id &&
        editDataFetched &&
        activeVisitTypes.length > 0 &&
        !petService.service_id
      ) {
        fetchVetClinicServiceDetailsForEdit(itemData.service_id);
      }
    }, [
      editMode,
      itemData?.service_id,
      editDataFetched,
      activeVisitTypes.length,
      petService.service_id,
    ]);

    const currentSlots = React.useMemo(() => {
      return generateDynamicSlotsForDate(petService.appointment_date);
    }, [petService.appointment_date, businessHours]);

    const fetchVetClinicServiceDetailsForEdit = async (serviceId) => {
      try {
        if (!serviceId) return;
        const originalPrice = itemData?.unit_price || itemData?.price || 0;
        const originalDiscount = itemData?.discount || 0;
        const originalGst = itemData?.gst || itemData?.gst_amount || 0;

        const hasValidPrice = originalPrice > 0;

        setPetService((prev) => ({
          ...prev,
          ...(hasValidPrice && {
            price: originalPrice,
            discount: originalDiscount,
            gst_amount: originalGst,
          }),
          appointment_to:
            itemData.technician_id ||
            itemData.appointment_to ||
            prev.appointment_to,
        }));

        await fetchVetClinicServiceDetails(serviceId, {
          preservePricing: hasValidPrice,
        });
      } catch (error) {
        console.error(
          "Failed to fetch vet clinic service details for edit:",
          error,
        );
        toast.error("Failed to fetch service details for editing");
      }
    };

    const fetchVetClinicServiceDetails = async (
      vendorServiceId,
      { preservePricing = false } = {},
    ) => {
      try {
        if (!vendorServiceId) return;

        const appointmentDate = petService.appointment_date || null;

        // ✅ Use POST instead of GET
        const serviceResponse = await httpService.post(
          `/vendorvetclinicservice/getvendorvetclinicservice/${vendorServiceId}`,
          {
            appointmentDate: appointmentDate, // ✅ Pass appointment date
          }
        );
        
        const detailedService = serviceResponse.data;

        if (!detailedService) {
          setPetService((prev) => ({
            ...prev,
            price: 0,
            duration: "",
            service_name: "",
            service_category_name: "",
            service_type_name: "Vet Clinic",
            service_details: null,
            service_id: "",
            vet_service: "",
          }));
          setServiceDetailData(null);
          return;
        }

        setServiceDetailData(detailedService);

        setPetService((prev) => ({
          ...prev,
          tax_percentage: detailedService.tax_percentage || 0,
          service_name: detailedService.name,
          service_category_name:
            detailedService.vet_category_name ||
            detailedService.service_category?.service_category_name ||
            "",
          service_type_name:
            detailedService.service_category?.service_type_name || "Vet Clinic",
          service_details: detailedService,
          service_id: detailedService._id,
          vet_service: detailedService._id,
        }));

        let duration = "";
        if (detailedService.duration) {
          if (detailedService.duration.duration_based_on === "flat") {
            const { hour = 0, minutes = 0 } = detailedService.duration.flat;
            if (hour > 0 && minutes > 0) duration = `${hour}h ${minutes}m`;
            else if (hour > 0) duration = `${hour}h`;
            else if (minutes > 0) duration = `${minutes}m`;
          } else if (petDetails?.size?.size_label) {
            const sizeDuration =
              detailedService.duration[
                petDetails.size.size_label.toLowerCase()
              ];
            if (sizeDuration) {
              const { hour = 0, minutes = 0 } = sizeDuration;
              if (hour > 0 && minutes > 0) duration = `${hour}h ${minutes}m`;
              else if (hour > 0) duration = `${hour}h`;
              else if (minutes > 0) duration = `${minutes}m`;
            }
          }
        }
        setPetService((prev) => ({ ...prev, duration }));

        if (!preservePricing) {
          try {
            const appointmentDate = petService.appointment_date || null;
            
            const priceResponse = await httpService.post(
              "/vendorbooking/getPriceCalculation?service_type=vet_clinic",
              { 
                vendor_service_id: detailedService._id,
                appointment_date: appointmentDate // ✅ Pass appointment date
              }
            );

            if (priceResponse && priceResponse.data !== undefined) {
              const responseData = priceResponse.data;

              const mrpPrice = responseData.mrp_price || responseData.price || 0;
              const salePrice = responseData.sale_price || responseData.price || 0;
              const discountPct = responseData.discount_percentage || 0;
              const discountAmt = responseData.discount_amount || 0;
              const taxAmount = responseData.discounted_tax || salePrice * (responseData.taxPercentage / 100) || 0;

              setPetService((prev) => ({
                ...prev,
                price: salePrice,
                basic_price: mrpPrice,
                discount: discountAmt,
                discount_percentage: discountPct,
                discount_amount: discountAmt,
                gst_amount: taxAmount,
                tax_percentage: responseData.taxPercentage || detailedService.tax_percentage || 0,
              }));
            } else {
              const fallbackPrice = detailedService.basic_price || 0;
              setPetService((prev) => ({
                ...prev,
                price: fallbackPrice,
                basic_price: fallbackPrice,
                discount: 0,
                discount_percentage: 0,
              }));
              toast.warn("Price calculation failed, using basic price");
            }
          } catch (priceError) {
            console.error("❌ [VetClinicDrawer] Price calculation error:", priceError);
            const fallbackPrice = detailedService.basic_price || 0;
            setPetService((prev) => ({
              ...prev,
              price: fallbackPrice,
              basic_price: fallbackPrice,
              discount: 0,
              discount_percentage: 0,
            }));
            toast.warn("Price calculation failed, using basic price");
          }
        }
      } catch (error) {
        console.error("❌ [VetClinicDrawer] Failed to fetch vet clinic service details:", error);
        toast.error("Failed to fetch service details");
        setPetService((prev) => ({ ...prev, price: 0, duration: "" }));
        setServiceDetailData(null);
      }
    };

    useEffect(() => {
      const salePrice = parseFloat(petService.price || "0") || 0;
      const total = salePrice;
      setPetService((prev) => ({
        ...prev,
        totalAmount: isNaN(total) ? "0.00" : total.toFixed(2),
      }));
    }, [petService.price, petService.discount, petService.gst]);

    const fetchPetDetails = async (petId) => {
      try {
        const response = await httpService.get(
          `/vendorpet/getvendorpet/${petId}`,
        );
        setPetDetails(response.data);
      } catch (error) {
        console.error("Failed to fetch pet details:", error);
      }
    };

    // ================ COMPUTED: FILTERED EMPLOYEE STAFFS =================
    const filteredEmployeeStaffs = staffs?.filter((staff) => {
      if (
        !petService.appointment_date ||
        !petService.appointment_start_time ||
        !petService.duration
      ) {
        return true; // no time selected yet, show all
      }

      const editIndex =
        editMode && itemData?._editIndex !== undefined
          ? itemData._editIndex
          : -1;

      const newStart = timeToMinutes(petService.appointment_start_time);
      const newDuration = parseDurationToMinutes(petService.duration);
      const newEnd = newStart + newDuration;

      const isConflicting = existingAppointments.some((appt, idx) => {
        if (idx === editIndex) return false;

        if (
          appt.item_type?.toLowerCase() === "boarding" ||
          appt.item_type?.toLowerCase() === "day_care"
        ) {
          return false;
        }

        if (
          appt.technician_id === staff._id &&
          appt.start_date === petService.appointment_date
        ) {
          if (!appt.start_time || !appt.duration) return false;

          const existingStart = timeToMinutes(appt.start_time);
          const existingDuration = parseDurationToMinutes(appt.duration);
          const existingEnd = existingStart + existingDuration;

          return newStart < existingEnd && newEnd > existingStart;
        }

        return false;
      });

      return !isConflicting;
    });

    // ================ EFFECT: UPDATE STAFF AVAILABILITY MESSAGE =========
    useEffect(() => {
      const hasSelection =
        petService.appointment_date &&
        petService.appointment_start_time &&
        petService.duration;

      if (hasSelection && filteredEmployeeStaffs.length === 0) {
        setStaffAvailableMessage(
          "No staff available for the selected time slot. Please choose a different time."
        );
      } else {
        setStaffAvailableMessage("");
      }
    }, [
      filteredEmployeeStaffs,
      petService.appointment_date,
      petService.appointment_start_time,
      petService.duration,
    ]);
    
    const handleSaveAsEnquiry = async () => {
      await handleSaveOrders();
      setSaveEnquiryClicked?.(true);
    };

    const handleSaveOrders = async () => {
      try {
        if (!petService?.pet_id) {
          toast.warn("Please select a pet");
          return;
        }
        if (!petService?.service_id) {
          toast.warn("Please select a visit type");
          return;
        }

        const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
        const selectedVendorService = vendorVetClinicServices.find(
          (s) => String(s._id) === String(petService.service_id),
        );
        const selectedStaff = staffs.find(
          (s) => s._id === petService.appointment_to,
        );

        // ✅ Collect selected form IDs and names
        const formIds = selectedForms.map((form) => form._id).filter(Boolean);
        const selectedFormNames = selectedForms
          .map((form) => form.form_name || form.name || "")
          .filter(Boolean);
        const hasMandatoryForm = selectedForms.some(
          (form) => getFormRequirement(form) === "mandatory",
        );

        const vetClinicData = {
          item_type: "vet_clinic",
          vendor_pet_id: petService.pet_id,
          service_id: petService.service_id,
          technician_id: petService.appointment_to,
          start_date: petService.appointment_date,
          start_time: petService.appointment_start_time,
          duration: petService.duration,
          quantity: 1,
          unit_price: parseFloat(petService.price) || 0, // ₹170
          mrp_price: parseFloat(petService.basic_price) || 0, // ₹200 - for display
          discount: parseFloat(petService.discount) || 0, // ₹30 - for display
          discount_percentage: parseFloat(petService.discount_percentage) || 0, // 15% - for display
          gst: parseFloat(petService.gst_amount) || 0, // ₹8.50
          notes: petService.symptoms || "",
          total_price: parseFloat(petService.totalAmount) || 0, // ₹178.50
          addons:
            petService.addons?.map((addon) => ({
              addon_id: addon.addon_id || addon._id,
              addon_price: parseFloat(addon.addon_price) || 0,
              name: addon.name || "",
            })) || [],
          pet_name: selectedPet?.name || "",
          pet_type:
            petDetails?.pet_type?.name || selectedPet?.pet_type?.name || "",
          pet_type_id: petDetails?.pet_type?._id || "",
          pet_size:
            petDetails?.size?.size_label || selectedPet?.size?.size_label || "",
          pet_breed:
            petDetails?.breed?.breed_name ||
            selectedPet?.breed?.breed_name ||
            "",
          service_name:
            petService.service_name || selectedVendorService?.name || "",
          service_category_name: selectedVendorService?.vet_category_name || "",
          service_type_name: petService.service_type_name || "Vet Clinic",
          employee_name: selectedStaff?.name || "",
          service_type_id: itemData?.service_type_id || serviceTypeId,
          tax_percentage: petService.tax_percentage,
          vet_details: { symptoms: petService.symptoms || "" },
          form_ids: formIds, // ✅ include forms
          form_names: selectedFormNames,
          form_name: selectedFormNames[0] || "",
          form_has_mandatory: hasMandatoryForm,
          ...(!itemData?._fromEnquiry &&
            itemData?.ref_id && { ref_id: itemData.ref_id }),
          ...(!itemData?._fromEnquiry && !itemData?.ref_id && itemData?._id
            ? { ref_id: itemData._id }
            : {}),
          ...(!itemData?._fromEnquiry &&
          !itemData?.ref_id &&
          !itemData?._id &&
          itemData?.id
            ? { ref_id: itemData.id }
            : {}),
        };

        if (itemData?._editIndex !== undefined) {
          vetClinicData._editIndex = itemData._editIndex;
          if (itemData?._originalVetClinic)
            vetClinicData._originalVetClinic = itemData._originalVetClinic;
        }

        if (fromCalendar) {
          if (itemData?._editIndex !== undefined) {
            onUpdate({
              ...vetClinicData,
              _editIndex: itemData._editIndex,
              _originalAppointment: itemData._originalAppointment,
            });
          } else {
            onUpdate(vetClinicData);
          }
          resetFormData();
          onClose();
          return;
        }

        const updatedFormData = {
          order_type: "offline",
          vendor_client_id: clientId,
          order_items: [
            {
              ...vetClinicData,
              ...(appointmentStatusRef?.current && {
                status: appointmentStatusRef.current,
              }),
            },
          ],
          ...(orderId && { order_id: orderId }),
          ...(itemData?.order_id && { order_id: itemData.order_id }),
        };

        const response = await httpService.post(
          "/vendororder/addVendorOrderOrUpdate",
          updatedFormData,
        );
        if (onUpdate) onUpdate(response.data);
        resetFormData();
        onClose();
      } catch (error) {
        console.error(
          "❌ [VetClinicDrawer] Error adding vet appointment:",
          error,
        );
        toast.error("Failed to add vet appointment. Please try again");
      }
    };

    // ─── Helper for Forms UI (isFormSent) ──────────────────────────────────
    const isFormSent = (form) => {
      const formName = form?.form_name || form?.formName || form?.name || "";
      if (!formName) return false;
      const normalized = normalizeConsentName(formName);
      return sentFormNames.some(
        (sentName) => normalizeConsentName(sentName) === normalized
      );
    };

    // ─── MAIN CONTENT ───────────────────────────────────────────────────────
    const mainContent = (
      <>
        {showNewPetForm ? (
          <AddNewPet
            isOpen={showNewPetForm}
            onClose={() => setShowNewPetForm(false)}
            onSave={(addedPetData) => {
              setNewPet({
                type: addedPetData.pet_type,
                name: addedPetData.name,
                gender: addedPetData.gender,
                size: addedPetData.size,
                breed: addedPetData.breed,
                age: addedPetData.age,
                birth_date: addedPetData.birthday,
                behavior_notes: addedPetData.behavior_notes,
                tags: addedPetData.tags,
              });
              setPetService((prev) => ({ ...prev, pet_id: addedPetData._id }));
              fetchPetDetails(addedPetData._id);
              setPets((prevPets) => [...prevPets, addedPetData]);
            }}
            clientId={clientId}
            isFullWidth={true}
            shouldHideSaveAndAdd={true}
            petId={isEditPet ? petDetails._id : null}
          />
        ) : (
          <>
            <Box
              sx={{ height: "100vh", display: "flex", flexDirection: "column" }}
            >
              {/* Header */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderBottom: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!renderAsContent && (
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FiArrowLeft className="w-5 h-5 text-gray-700" />
                  </button>
                )}
              </Box>

              {/* Scrollable body */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: 2,
                  pb: 10,
                }}
              >
                <Grid container spacing={2}>
                  {/* ── Pet Selection ─────────────────────────────────────── */}
                  {!itemData?.ref_id && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                      >
                        <PetsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        Pet <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" gap={1}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={petService.pet_id || ""}
                          onChange={(e) => {
                            if (isPetDisabled) return;
                            const petId = e.target.value;
                            setPetService((prev) => ({
                              ...prev,
                              pet_id: petId,
                            }));
                            if (petId) fetchPetDetails(petId);
                          }}
                          disabled={isPetDisabled}
                          SelectProps={{ displayEmpty: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PetsIcon
                                  sx={{
                                    color: isPetDisabled
                                      ? "#9ca3af"
                                      : "#6b7280",
                                    fontSize: 18,
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: isPetDisabled
                                ? "#f9fafb"
                                : "white",
                              border: "1px solid #e5e7eb",
                              "&:hover": {
                                borderColor: isPetDisabled
                                  ? "#e5e7eb"
                                  : "#3b82f6",
                              },
                              "&.Mui-focused": {
                                borderColor: isPetDisabled
                                  ? "#e5e7eb"
                                  : "#3b82f6",
                              },
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#374151",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {isPetDisabled ? "Pet pre-selected" : "Select pet"}
                          </MenuItem>
                          {pets
                            .filter(
                              (pet) => pet.status?.toLowerCase() !== "inactive",
                            )
                            ?.map((pet) => (
                              <MenuItem key={pet._id} value={pet._id}>
                                {pet.name}
                                {pet.breed?.breed_name
                                  ? ` - ${pet.breed.breed_name}`
                                  : ""}
                                {pet.age ? ` (${pet.age} yrs)` : ""}
                              </MenuItem>
                            ))}
                        </TextField>
                        {!isPetDisabled && (
                          <>
                            {petDetails?._id && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setIsEditPet(true);
                                  setShowNewPetForm(true);
                                }}
                                sx={{
                                  minWidth: "60px",
                                  height: "40px",
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  background:
                                    "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                  border: "none",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.25)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                    boxShadow:
                                      "0 6px 16px rgba(99, 102, 241, 0.35)",
                                    transform: "translateY(-1px)",
                                    border: "none",
                                  },
                                }}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setIsEditPet(false);
                                setShowNewPetForm(true);
                              }}
                              startIcon={<MdPets />}
                              sx={{
                                minWidth: "100px",
                                height: "40px",
                                textTransform: "none",
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "13px",
                                boxShadow:
                                  "0 4px 12px rgba(99, 102, 241, 0.25)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                  boxShadow:
                                    "0 6px 16px rgba(99, 102, 241, 0.35)",
                                  transform: "translateY(-1px)",
                                  border: "none",
                                },
                              }}
                            >
                              Add Pet
                            </Button>
                          </>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* ── Pet Card ──────────────────────────────────────────── */}
                  {petDetails.length != 0 && (
                    <Grid item xs={12}>
                      <PetCard
                        pet={petDetails}
                        onEdit={() => {
                          setIsEditPet(true);
                          setShowNewPetForm(true);
                        }}
                        serviceName={
                          serviceDetailData?.service_category
                            ?.service_category_name ||
                          petService.service_category_name ||
                          ""
                        }
                      />
                    </Grid>
                  )}

                  {/* ── Appointment Date ──────────────────────────────────── */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiCalendar className="w-4 h-4 text-blue-600" />
                      Appointment Date <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      lang="en-IN"
                      fullWidth
                      size="small"
                      value={petService.appointment_date}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const previousValue = petService.appointment_date;

                        const isValid = handlePetServiceChange(
                          "appointment_date",
                          newValue,
                        );

                        if (!isValid) {
                          e.target.value = previousValue || "";
                        }
                      }}
                      inputProps={{
                       // min: new Date().toISOString().split("T")[0],
                       min: minDateStr,
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 4px 12px rgba(59,130,246,0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Appointment Time ──────────────────────────────────── */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiClock className="w-4 h-4 text-green-600" />
                      Appointment Time <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.appointment_start_time || ""}
                      onChange={(e) =>
                        handlePetServiceChange(
                          "appointment_start_time",
                          e.target.value,
                        )
                      }
                      SelectProps={{
                        MenuProps: {
                          style: { zIndex: 99999 },
                          disablePortal: false,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#10b981",
                            boxShadow: "0 4px 12px rgba(16,185,129,0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#10b981",
                            boxShadow: "0 0 0 3px rgba(16,185,129,0.1)",
                          },
                        },
                      }}
                    >
                      {currentSlots
                        .filter(
                          (slot) =>
                            slot === petService.appointment_start_time ||
                            !isPastTimeSlot(slot, petService.appointment_date),
                        )
                        .map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>

                  {/* ── Visit Type ───────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        className="flex items-center gap-2"
                      >
                        <MedicalServicesIcon
                          sx={{ fontSize: 16, color: "#10b981" }}
                        />
                        Service <span style={{ color: "red" }}>*</span>
                      </Typography>
                      {serviceDetailData && petService.service_id && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setShowServiceDetailsModal(true)}
                          sx={{
                            minWidth: "auto",
                            p: 0.5,
                            color: "#6b7280",
                            "&:hover": { color: "#3b82f6" },
                          }}
                        >
                          <FiEye size={18} />
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            View Details
                          </Typography>
                        </Button>
                      )}
                    </Box>

                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={petService.service_id || ""}
                        onChange={(e) => {
                          const vendorServiceId = e.target.value;
                          setPetService((prev) => ({
                            ...prev,
                            vet_service: vendorServiceId,
                            service_id: vendorServiceId,
                          }));
                          if (vendorServiceId) {
                            fetchVetClinicServiceDetails(vendorServiceId);
                          } else {
                            setPetService((prev) => ({
                              ...prev,
                              service_name: "",
                              service_category_name: "",
                              service_type_name: "",
                              price: 0,
                              duration: "",
                            }));
                            setServiceDetailData(null);
                          }
                          setShowServiceDetailsModal(false);
                        }}
                        disabled={!petDetails?._id}
                        SelectProps={{
                          MenuProps: {
                            style: { zIndex: 99999 },
                            disablePortal: false,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MedicalServicesIcon
                                sx={{ color: "#6b7280", fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                              borderColor: "#10b981",
                              boxShadow: "0 4px 12px rgba(16,185,129,0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#10b981",
                              boxShadow: "0 0 0 3px rgba(16,185,129,0.1)",
                            },
                          },
                        }}
                      >
                        {activeVisitTypes.length > 0 ? (
                          activeVisitTypes.map((service) => {
                            const mrpPrice = Number(
                              service?.mrp_price || service?.basic_price || 0,
                            );
                            const salePrice = Number(
                              service?.sale_price || service?.basic_price || 0,
                            );
                            const discountPct =
                              service?.discount_percentage || 0;
                            const isFree = discountPct === 100;
                            const hasDiscount =
                              (salePrice < mrpPrice && discountPct > 0) ||
                              isFree;

                            return (
                              <MenuItem
                                key={service._id}
                                value={service._id}
                                sx={{
                                  py: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>{service?.name}</span>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {hasDiscount ? (
                                    <>
                                      <Typography
                                        component="span"
                                        sx={{
                                          textDecoration: "line-through",
                                          color: "#9ca3af",
                                          fontSize: "12px",
                                        }}
                                      >
                                        ₹{mrpPrice}
                                      </Typography>
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: "#10b981",
                                          fontWeight: "bold",
                                          fontSize: "14px",
                                        }}
                                      >
                                        {isFree ? "FREE" : `₹${salePrice}`}
                                      </Typography>
                                      <Chip
                                        label={`${discountPct}% OFF`}
                                        size="small"
                                        sx={{
                                          backgroundColor: isFree
                                            ? "#dcfce7"
                                            : "#fef3c7",
                                          color: isFree ? "#166534" : "#d97706",
                                          fontSize: "10px",
                                          height: "20px",
                                          "& .MuiChip-label": { px: 1 },
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#10b981",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      }}
                                    >
                                      ₹{mrpPrice}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value="" disabled>
                            {!petDetails?._id
                              ? "Please select a pet first"
                              : "No active visit types available for this pet"}
                          </MenuItem>
                        )}
                      </TextField>

                      {/* ✅ ADD THIS BUTTON */}
                      {!isEmployee && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          // Close the current drawer (optional, but recommended)
                          resetFormData();
                          // onClose();
                          // Navigate to the vet listings page
                          navigate("/admin/service-management/vet_clinic")
                        }}
                        startIcon={<AddIcon />}
                        sx={{
                          minWidth: "130px",
                          height: "40px",
                          textTransform: "none",
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "13px",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            boxShadow: "0 6px 16px rgba(16, 185, 129, 0.35)",
                            transform: "translateY(-1px)",
                          },
                          "& .MuiButton-startIcon": {
                            marginRight: "4px",
                            "& svg": { fontSize: "18px" },
                          },
                        }}
                      >
                        Add Service
                      </Button>
                      )}
                    </Box>
                  </Grid>

                  {/* ── Price Breakdown ──────────────────────────────────── */}
                  {serviceDetailData && petService.service_id && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          background:
                            "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                          border: "1px solid #e2e8f0",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "center",
                            mb: 1,
                            pb: 1,
                            borderBottom: "1px dashed #cbd5e1",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            <strong>Duration:</strong>{" "}
                            {petService.duration || "N/A"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                            |
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            <strong>Service charge:</strong>{" "}
                            {petService.tax_percentage || 0}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            <strong>Base price:</strong> ₹
                            {serviceDetailData?.basic_price ||
                              petService.basic_price ||
                              0}
                          </Typography>

                          {petService.discount_percentage > 0 && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ color: "#cbd5e1" }}
                              >
                                |
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#475569" }}
                              >
                                <strong>Discount:</strong>{" "}
                                {petService.discount_percentage}% (₹
                                {petService.discount || 0})
                              </Typography>
                            </>
                          )}

                          <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                            |
                          </Typography>

                          {petService.discount_percentage === 100 ? (
                            <Typography
                              variant="body2"
                              sx={{ color: "#10b981", fontWeight: "bold" }}
                            >
                              <strong>Amount:</strong> FREE
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "#475569", fontWeight: "bold" }}
                            >
                              <strong>Amount:</strong> ₹
                              {(() => {
                                const amt = petService.price || petService.sale_price || 0;
                                const num = parseFloat(amt) || 0;
                                return num % 1 === 0 ? num : num.toFixed(2);
                              })()}
                              {petService.gst_amount > 0 && (
                                <span
                                  style={{
                                    color: "#475569",
                                    fontWeight: "bold",
                                    marginLeft: "4px",
                                  }}
                                >
                                  (+ ₹{parseFloat(petService.gst_amount).toFixed(2)} includes taxes)
                                </span>
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* ── Veterinarian ──────────────────────────────────────── */}
                  {!isEmployee && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                      >
                        <PersonIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                        Veterinarian{" "}
                        <span className="italic text-sm font-light">
                          {staffAvailableMessage}
                        </span>
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={petService.appointment_to || ""}
                        SelectProps={{
                          MenuProps: {
                            style: { zIndex: 99999 },
                            disablePortal: false,
                          },
                        }}
                        onChange={(e) =>
                          handlePetServiceChange(
                            "appointment_to",
                            e.target.value,
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon
                                sx={{ color: "#6b7280", fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                              borderColor: "#8b5cf6",
                              boxShadow: "0 4px 12px rgba(139,92,246,0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#8b5cf6",
                              boxShadow: "0 0 0 3px rgba(139,92,246,0.1)",
                            },
                          },
                        }}
                      >
                        {/* Placeholder option */}
                        <MenuItem value="" disabled>
                          {filteredEmployeeStaffs.length === 0
                            ? "No staff available for this time slot"
                            : "Select Veterinarian"}
                        </MenuItem>
                        
                        {/* Use precomputed filteredEmployeeStaffs */}
                        {filteredEmployeeStaffs.map((type) => (
                          <MenuItem key={type._id} value={type._id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {/* ── Service Notes ────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        fontSize: "15px",
                        color: "#374151",
                        fontWeight: 600,
                        textTransform: "none",
                        p: 0,
                        mb: 0.5,
                      }}
                      onClick={() => setShowServiceNotes(!showServiceNotes)}
                      startIcon={
                        showServiceNotes ? (
                          <CancelIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <AddIcon sx={{ fontSize: 16 }} />
                        )
                      }
                    >
                      {showServiceNotes ? "Cancel Notes" : "Add Service Notes"}
                    </Button>

                    {showServiceNotes && (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={petService.symptoms}
                        onChange={(e) =>
                          handlePetServiceChange("symptoms", e.target.value)
                        }
                        placeholder="Describe symptoms, concerns, or reason for the visit..."
                        sx={{
                          mb: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                              borderColor: "#6b7280",
                              boxShadow: "0 4px 12px rgba(107,114,128,0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#6b7280",
                              boxShadow: "0 0 0 3px rgba(107,114,128,0.1)",
                            },
                          },
                        }}
                      />
                    )}
                  </Grid>

                  {/* ─── FORMS SECTION (direct integration) ──────────────── */}
                  <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <NotesIcon sx={{ fontSize: 16, color: "#14b8a6" }} />
                      Forms
                    </Typography>
                    <Autocomplete
                      multiple
                      openOnFocus
                      loading={loadingForms}
                      options={availableForms}
                      value={selectedForms}
                      inputValue={formInputValue}
                      onInputChange={(event, newInputValue) =>
                        setFormInputValue(newInputValue)
                      }
                      getOptionLabel={(option) => option.form_name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                      }
                      // ✅ Only filter by search text — do NOT exclude selected items.
                      // Excluding selected items from `options` breaks MUI's internal value
                      // tracking and prevents optional forms from being selected.
                      filterOptions={(options, state) => {
                        const input = String(state.inputValue || "")
                          .trim()
                          .toLowerCase();
                        if (!input) return options;
                        return options.filter((option) =>
                          String(option?.form_name || "")
                            .toLowerCase()
                            .includes(input),
                        );
                      }}
                      // ✅ Disable mandatory forms in the dropdown so they can't be deselected.
                      getOptionDisabled={(option) =>
                        option.requirement === "mandatory"
                      }
                      onChange={(event, newValue) => {
                        // ✅ Mandatory forms are always locked (getOptionDisabled prevents
                        // MUI from removing them via the dropdown). But in case MUI strips
                        // them anyway (e.g. on clear), we always re-inject them here.
                        const mandatoryForms = availableForms.filter(
                          (f) => f.requirement === "mandatory",
                        );
                        const mandatoryIds = new Set(
                          mandatoryForms.map((f) => f._id),
                        );

                        const merged = [
                          ...mandatoryForms,
                          ...(newValue || []).filter(
                            (f) => !mandatoryIds.has(f._id),
                          ),
                        ];

                        // Deduplicate by _id
                        const unique = Array.from(
                          new Map(
                            merged
                              .filter(Boolean)
                              .map((item) => [item._id, item]),
                          ).values(),
                        );
                        setSelectedForms(unique);
                        setFormInputValue("");
                      }}
                      groupBy={() => "Available Forms"}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          const isMandatory =
                            option.requirement === "mandatory";
                          const sent = isFormSent(option);

                          const chipSx = isMandatory
                            ? {
                                borderRadius: "16px",
                                backgroundColor: "#fee2e2",
                                border: "1px solid #fecaca",
                                height: "32px",
                                "& .MuiChip-label": {
                                  fontSize: "14px",
                                  color: "#b91c1c",
                                },
                              }
                            : {
                                borderRadius: "16px",
                                backgroundColor: "#ffedd5",
                                border: "1px solid #fed7aa",
                                height: "32px",
                                "& .MuiChip-label": {
                                  fontSize: "14px",
                                  color: "#b45309",
                                },
                              };

                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  <span>{option.form_name}</span>
                                  {sent && (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 0.75,
                                        py: 0.15,
                                        borderRadius: "999px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: "#166534",
                                        backgroundColor: "#dcfce7",
                                        border: "1px solid #bbf7d0",
                                      }}
                                    >
                                      Sent
                                    </Box>
                                  )}
                                </Box>
                              }
                              {...tagProps}
                              onDelete={
                                isMandatory ? undefined : tagProps.onDelete
                              }
                              sx={chipSx}
                            />
                          );
                        })
                      }
                      renderOption={(props, option) => {
                        const { key, ...restProps } = props;
                        const isMandatory = option.requirement === "mandatory";
                        const sent = isFormSent(option);

                        const badgeSx = isMandatory
                          ? {
                              backgroundColor: "#fee2e2",
                              color: "#b91c1c",
                              border: "1px solid #fecaca",
                            }
                          : {
                              backgroundColor: "#ffedd5",
                              color: "#b45309",
                              border: "1px solid #fed7aa",
                            };

                        return (
                          <li key={key} {...restProps}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: 1.5,
                                py: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {option.form_name}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    ...badgeSx,
                                  }}
                                >
                                  {isMandatory ? "Mandatory" : "Optional"}
                                </Box>
                              </Box>
                              {sent && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#166534",
                                    backgroundColor: "#dcfce7",
                                    border: "1px solid #bbf7d0",
                                  }}
                                >
                                  Sent
                                </Box>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={
                            selectedForms.length === 0
                              ? "Search and select forms..."
                              : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "white",
                              border: "1px solid #e5e7eb",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: "#10b981",
                                boxShadow:
                                  "0 4px 12px rgba(16, 185, 129, 0.15)",
                              },
                              "&.Mui-focused": {
                                borderColor: "#10b981",
                                boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                              },
                            },
                          }}
                        />
                      )}
                    />

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<FiSend />}
                        disabled={sendingForms || selectedForms.length === 0}
                        onClick={handleSendSelectedForms}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          boxShadow: 2,
                          minWidth: 200,
                          width: "fit-content",
                        }}
                      >
                        {sendingForms ? "Sending..." : "Send Selected Form(s)"}
                      </Button> */}
                    </Box>
                  </Grid>
                  {/* ─── END FORMS SECTION ────────────────────────────────── */}
                </Grid>
              </Box>

              {/* ── Bottom Action Buttons ──────────────────────────────────── */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid #eee",
                  boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        resetFormData();
                        onClose();
                      }}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {!editMode && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveAsEnquiry}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          backgroundColor: "#7E5FF5",
                          "&:hover": { backgroundColor: "#6D4EE5" },
                        }}
                      >
                        Save Enquiry
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {editMode ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        disabled={
                          (
                            isEmployee
                              ? (petService.appointment_to === "" || !petDetails.size?.size_label)
                              : (filteredEmployeeStaffs.length === 0 || !petDetails.size?.size_label)
                          )
                        }
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                        }}
                      >
                        Update
                      </Button>
                    ) : fromCalendar ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<AddIcon />}
                        disabled={
                          (
                            isEmployee
                              ? (petService.appointment_to === "" || !petDetails.size?.size_label)
                              : (filteredEmployeeStaffs.length === 0 || !petDetails.size?.size_label)
                          )
                        }
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                        }}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* ── Service Details Modal ────────────────────────────────────── */}
            {showServiceDetailsModal && serviceDetailData && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
                onClick={() => setShowServiceDetailsModal(false)}
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    maxWidth: 400,
                    width: "100%",
                    maxHeight: "80vh",
                    overflow: "auto",
                    p: 3,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#1f2937" }}
                    >
                      {serviceDetailData?.name || "Service Details"}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setShowServiceDetailsModal(false)}
                      sx={{ minWidth: "auto", p: 0.5, color: "#6b7280" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  {serviceDetailData?.description && (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#6b7280", mb: 0.5 }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "#374151" }}
                      >
                        {serviceDetailData.description}
                      </Typography>
                    </>
                  )}

                  {serviceDetailData?.service_inclusions?.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#6b7280", mb: 0.5 }}
                      >
                        What's Included
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {serviceDetailData.service_inclusions.map(
                          (inclusion, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ color: "#374151" }}
                              >
                                {inclusion.inclusion_headings}:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280", ml: 2 }}
                              >
                                {inclusion.selected_options?.join(", ") ||
                                  "None"}
                              </Typography>
                            </Box>
                          ),
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </>
    );

    return (
      <>
        {renderAsContent ? (
          mainContent
        ) : (
          <Drawer
            anchor="right"
            open={isOpen}
            onClose={() => {
              resetFormData();
              onClose();
            }}
            ModalProps={{ style: { zIndex: 10000 } }}
            PaperProps={{
              sx: {
                width: isMobile ? "100%" : "70vw",
                maxWidth: 800,
                p: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowX: "hidden",
              },
            }}
          >
            {mainContent}
          </Drawer>
        )}

      </>
    );
  }
);

VetClinicDrawer.displayName = "VetClinicDrawer";
export default VetClinicDrawer;